/* eslint-disable */
import { Box, Button, Typography, Avatar, Chip, IconButton } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import ApiEndpoint from "../../config/ApiEndpoint";
import ApiServices from "../../config/ApiServices";
import { toast } from "react-toastify";
import { PageHeader, Card, DataTable, StatusBadge, ActionButton, TouchFriendlyTooltip } from "../shared";
import { formatClubType } from "../../Utils/displayLabels";
import { shouldShowCommunityJoinRequests } from "../../Utils/func";
import moment from "moment";
import HelpIcon from "@mui/icons-material/HelpOutline";

const JoinRequests = (props) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const token = props?.props?.profile?.token;
  const userType =
    props?.props?.profile?.user_type || props?.props?.profile?.data?.user_type;
  const showCommunityJoinRequests = shouldShowCommunityJoinRequests(
    props?.props?.profile
  );
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = {
    "Content-Type": "application/json",
    "x-access-token": token,
  };

  const loadRequests = async () => {
    if (!token) return;
    setLoading(true);
    const data = await ApiServices.GetApiCall(
      `${ApiEndpoint.MASTER_ORG_REQUESTS}?status=pending`,
      headers
    );
    setLoading(false);
    if (data?.status) {
      setRequests(data.data || []);
    } else if (data?.message) {
      toast.error(data.message);
    }
  };

  const handleRequest = async (requestId, action) => {
    if (!token) return;
    const endpoint =
      action === "approve"
        ? ApiEndpoint.MASTER_ORG_REQUEST_APPROVE
        : ApiEndpoint.MASTER_ORG_REQUEST_DENY;
    props.setIsLoaded(true);
    const data = await ApiServices.PostApiCall(
      endpoint,
      JSON.stringify({ request_id: requestId }),
      headers
    );
    props.setIsLoaded(false);
    if (data?.status) {
      toast.success(data.message || "Request updated");
      loadRequests();
    } else {
      toast.error(data?.message || "Failed to update request");
    }
  };

  // Table columns
  const columns = [
    {
      field: "id",
      label: "ID",
      width: 70,
    },
    {
      field: "name",
      label: "Club",
      render: (_, row) => {
        const subOrg = row.subOrg || {};
        // Get logo URL directly from API response (backend now includes logo_url)
        const logoUrl = subOrg.logo_url || "";
        const orgName = subOrg.name || "";
        
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={logoUrl}
              sx={{
                width: 40,
                height: 40,
                bgcolor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
                color: isDark ? "#818cf8" : "#1976d2",
                fontSize: "0.875rem",
                fontWeight: 600,
              }}
            >
              {orgName[0]?.toUpperCase() || "O"}
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 600, color: isDark ? "#ffffff" : "#1a202c", fontSize: "0.875rem" }}>
                {orgName || "-"}
              </Typography>
              <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
                {subOrg.user?.email || "-"}
              </Typography>
            </Box>
          </Box>
        );
      },
    },
    {
      field: "school_type",
      label: "Club Type",
      render: (_, row) => {
        const subOrg = row.subOrg || {};
        const schoolType = formatClubType(subOrg.school_type || "Organisation");
        return (
          <Chip
            label={schoolType}
            size="small"
            sx={{
              backgroundColor: isDark ? "rgba(99, 102, 241, 0.15)" : "rgba(25, 118, 210, 0.1)",
              color: isDark ? "#818cf8" : "#1976d2",
              fontWeight: 500,
              fontSize: "0.75rem",
              textTransform: "capitalize",
            }}
          />
        );
      },
    },
    {
      field: "contact",
      label: "Contact",
      render: (_, row) => {
        const subOrg = row.subOrg || {};
        const user = subOrg.user || {};
        const firstName = user.first_name || "";
        const lastName = user.last_name || "";
        const email = user.email || "-";
        const fullName = `${firstName} ${lastName}`.trim() || "-";
        
        return (
          <Box>
            <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#ffffff" : "#1a202c", fontWeight: 500 }}>
              {fullName}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: isDark ? "#718096" : "#718096" }}>
              {email}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "requested_at",
      label: "Requested",
      render: (value) => {
        if (!value) return "-";
        const formatted = moment(value).format("MMM DD, YYYY");
        return (
          <Typography sx={{ fontSize: "0.875rem", color: isDark ? "#cbd5e1" : "#4a5568" }}>
            {formatted}
          </Typography>
        );
      },
    },
    {
      field: "status",
      label: "Status",
      render: (value) => <StatusBadge status={value || "pending"} />,
    },
  ];

  const renderActions = (row) => (
    <Box sx={{ display: "flex", gap: 1 }}>
      <ActionButton
        variant="secondary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleRequest(row.id, "deny");
        }}
      >
        Deny
      </ActionButton>
      <ActionButton
        variant="primary"
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          handleRequest(row.id, "approve");
        }}
      >
        Approve
      </ActionButton>
    </Box>
  );

  useEffect(() => {
    if (userType === "master_org") {
      loadRequests();
    }
  }, [userType, token]);

  useEffect(() => {
    if (userType === "master_org" && !showCommunityJoinRequests) {
      navigate("/master-org/dashboard", { replace: true });
    }
  }, [userType, showCommunityJoinRequests, navigate]);

  if (userType !== "master_org") {
    return (
      <Box>
        <PageHeader
          title="Join Requests"
          subtitle="Access denied. This page is for club partner accounts."
        />
      </Box>
    );
  }

  if (!showCommunityJoinRequests) {
    return null;
  }

  return (
    <Box>
      <PageHeader
        title="Join Requests"
        subtitle={`Approve or deny pending club requests. ${requests.length} pending.`}
      >
        <TouchFriendlyTooltip
          title={
            <Box sx={{ maxWidth: 320 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                Join requests flow
              </Typography>
              <Typography variant="body2">
                • When a club uses one of your <strong>Invite Codes</strong>, their club appears here as a join request.
              </Typography>
              <Typography variant="body2">
                • Review the details, then <strong>Approve</strong> to connect them as a club or <strong>Deny</strong> to reject.
              </Typography>
              <Typography variant="body2">
                • Approved clubs will then show on the <strong>Clubs</strong> page.
              </Typography>
            </Box>
          }
          arrow
        >
          <IconButton
            size="small"
            sx={{ color: "#4a5568" }}
          >
            <HelpIcon fontSize="small" />
          </IconButton>
        </TouchFriendlyTooltip>
      </PageHeader>
      
      <Card>
        <DataTable
          columns={columns}
          data={requests}
          loading={loading}
          actions={renderActions}
          emptyMessage="No pending requests"
          pagination={false}
          mobileTitleColumn="name"
          mobileHiddenColumns={["id"]}
        />
      </Card>
    </Box>
  );
};

export default JoinRequests;
